import * as React from 'react'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faTwitch } from '@fortawesome/free-brands-svg-icons'
import {
  faBan,
  faBolt,
  faCircleExclamation,
  faCircleInfo,
  faCircleQuestion,
  faClapperboard,
  faClock,
  faEye,
  faGlobe,
  faLayerGroup,
  faLink,
  faList,
  faRotate,
  faStar,
  faVolumeHigh,
  faWindowMaximize,
} from '@fortawesome/free-solid-svg-icons'
import ActionPreview, {
  ActionSetEditorPreview,
  ConditionalEditorPreview,
  ActionStack,
  NestedActions,
  PreviewField,
  PreviewInput,
  PreviewInputRow,
  PreviewMessageField,
  QueueOptionsPreview,
  VariableToken,
} from './components/ActionPreview'

export type GuideSection = {
  id: string
  title: string
  paragraphs?: React.ReactNode[]
  steps?: React.ReactNode[]
  preview?: React.ReactNode
  note?: React.ReactNode
}

export type GuideDefinition = {
  slug: string
  title: string
  tag: string
  summary: string
  icon: IconDefinition
  level: 'Start here' | 'Intermediate' | 'Advanced'
  time: string
  outcome: React.ReactNode[]
  sections: GuideSection[]
  related: string[]
}

const clipAndChatPreview = (
  <ActionStack>
    <ActionPreview kind='twitch' title='Twitch' subtitle='Create Clip' icon={faClapperboard as IconDefinition}>
      <PreviewInputRow>
        <PreviewInput label='Clip Title (optional)' chevron>
          {'{title}'}
        </PreviewInput>
        <PreviewInput label='Clip Duration (seconds)'>60</PreviewInput>
      </PreviewInputRow>
      <NestedActions caption='If clipping succeeds (may take up to 15 seconds)' toolbar>
        <ActionPreview kind='twitch' title='Twitch' subtitle='Chat Message' icon={faTwitch as IconDefinition}>
          <PreviewMessageField>
            @<VariableToken>{'{user:name}'}</VariableToken> your clip has been created: <VariableToken>{'{clip:url}'}</VariableToken>
          </PreviewMessageField>
        </ActionPreview>
      </NestedActions>
    </ActionPreview>
  </ActionStack>
)

const timedObsPreview = (
  <ActionStack>
    <ActionPreview kind='obs' title='OBS' subtitle='Enable Source'>
      <PreviewInputRow>
        <PreviewInput label='Scene Name' chevron>
          {'Overlays'}
        </PreviewInput>
        <PreviewInput label='Source Name' chevron>
          {'ALDI'}
        </PreviewInput>
      </PreviewInputRow>
    </ActionPreview>
    <ActionPreview kind='delay' title='Delay' icon={faClock as IconDefinition} note='5 seconds'>
      <PreviewInput label='Delay Time (in milliseconds)' chevron>
        {'5000'}
      </PreviewInput>
    </ActionPreview>
    <ActionPreview kind='obs' title='OBS' subtitle='Disable Source'>
      <PreviewInputRow>
        <PreviewInput label='Scene Name' chevron>
          {'Overlays'}
        </PreviewInput>
        <PreviewInput label='Source Name' chevron>
          {'ALDI'}
        </PreviewInput>
      </PreviewInputRow>
    </ActionPreview>
    <ActionPreview kind='delay' title='Delay' icon={faClock as IconDefinition} note='0.1 seconds'>
      <PreviewInput label='Delay Time (in milliseconds)' chevron>
        100
      </PreviewInput>
    </ActionPreview>
  </ActionStack>
)

const timedObsPreviewVariable = (
  <ActionStack>
    <ActionPreview kind='obs' title='OBS' subtitle='Enable Source'>
      <PreviewInputRow>
        <PreviewInput label='Scene Name' chevron>
          {'{scene}'}
        </PreviewInput>
        <PreviewInput label='Source Name' chevron>
          {'{source}'}
        </PreviewInput>
      </PreviewInputRow>
    </ActionPreview>
    <ActionPreview kind='delay' title='Delay' icon={faClock as IconDefinition}>
      <PreviewInput label='Delay Time (in milliseconds)' chevron>
        {'{time}'}
      </PreviewInput>
    </ActionPreview>
    <ActionPreview kind='obs' title='OBS' subtitle='Disable Source'>
      <PreviewInputRow>
        <PreviewInput label='Scene Name' chevron>
          {'{scene}'}
        </PreviewInput>
        <PreviewInput label='Source Name' chevron>
          {'{source}'}
        </PreviewInput>
      </PreviewInputRow>
    </ActionPreview>
    <ActionPreview kind='delay' title='Delay' icon={faClock as IconDefinition} note='0.1 seconds'>
      <PreviewInput label='Delay Time (in milliseconds)' chevron>
        100
      </PreviewInput>
    </ActionPreview>
  </ActionStack>
)

const timedObsActionSetPreview = (
  <ActionSetEditorPreview
    name='Show OBS Source for Time'
    variables={[
      { name: 'source', type: 'Source' },
      { name: 'time', type: 'Number' },
      { name: 'scene', type: 'Scene' },
    ]}
  >
    {timedObsPreviewVariable}
  </ActionSetEditorPreview>
)

const viewerCounterPreview = (
  <ActionStack>
    <ActionPreview kind='variable' title='Variable' subtitle='Increment Variable'>
      <PreviewField label='Variable Name'>
        boop_<VariableToken>{'{@receiver:name}'}</VariableToken>
      </PreviewField>
      <PreviewField label='Increment By'>1</PreviewField>
    </ActionPreview>
    <ActionPreview kind='contextwindow' title='Context Window' icon={faWindowMaximize as IconDefinition}>
      <NestedActions caption='Actions in the new context'>
        <ActionPreview
          kind='conditional'
          title='Conditional Actions'
          icon={faCircleQuestion as IconDefinition}
          details={
            <>
              <VariableToken>{'{boop_{@receiver:name}}'}</VariableToken> equals 1
            </>
          }
        />
      </NestedActions>
    </ActionPreview>
  </ActionStack>
)

const reusableRoutineBodyPreview = (
  <ActionStack>
    <ActionPreview kind='twitch' title='Twitch' subtitle='Chat Message' icon={faTwitch as IconDefinition}>
      <PreviewMessageField>
        <VariableToken>{'{message}'}</VariableToken>, @<VariableToken>{'{@user:name}'}</VariableToken>!
      </PreviewMessageField>
    </ActionPreview>
    <ActionPreview kind='obs' title='OBS' subtitle='Enable Source' details='Show Celebration overlay' />
  </ActionStack>
)

const reusableRoutineDefinitionPreview = (
  <ActionSetEditorPreview
    name='Celebrate user'
    variables={[
      { name: 'user', type: 'Username' },
      { name: 'message', type: 'Text' },
    ]}
  >
    {reusableRoutineBodyPreview}
  </ActionSetEditorPreview>
)

const reusableRoutineCallPreview = (
  <ActionStack>
    <ActionPreview kind='actionset' title='Run Action Set' icon={faList as IconDefinition}>
      <PreviewInput label='Action Set to Run' chevron>
        Celebrate user
      </PreviewInput>
      <PreviewField label='Username'>
        <VariableToken>{'{user:name}'}</VariableToken>
      </PreviewField>
      <PreviewField label='Text' chevron={false}>
        Great job!
      </PreviewField>
    </ActionPreview>
  </ActionStack>
)

const chainPreview = (
  <ActionStack>
    <ActionPreview kind='twitch' title='Twitch' subtitle='Chat Message' icon={faTwitch as IconDefinition} details='Thanks for the follow!' />
    <ActionPreview kind='sound' title='Play Sound' icon={faVolumeHigh as IconDefinition} details='celebration.mp3' />
    <ActionPreview kind='delay' title='Delay' icon={faClock as IconDefinition} details='3000 milliseconds' />
    <ActionPreview kind='obs' title='OBS' subtitle='Disable Source' details='Hide Celebration overlay' />
  </ActionStack>
)

const variablesPreview = (
  <ActionStack>
    <ActionPreview kind='twitch' title='Twitch' subtitle='Chat Message' icon={faTwitch as IconDefinition}>
      <PreviewMessageField>
        Thanks <VariableToken>{'{@user:name}'}</VariableToken>! Your clip is <VariableToken>{'{clip:url}'}</VariableToken>.
      </PreviewMessageField>
    </ActionPreview>
  </ActionStack>
)

const conditionalPreview = (
  <ActionStack>
    <ActionPreview kind='conditional' title='Conditional Actions' icon={faCircleQuestion as IconDefinition}>
      <ConditionalEditorPreview
        rules={[
          {
            left: 'Is valid Twitch user',
            compare: 'Equals',
            right: 'User is a valid Twitch user',
          },
        ]}
        whenMatched={
          <ActionPreview
            kind='twitch'
            title='Twitch'
            subtitle='Chat Message'
            icon={faTwitch as IconDefinition}
            details={
              <>
                Hello, <VariableToken>{'{@user:name}'}</VariableToken>!
              </>
            }
          />
        }
        whenNotMatched={<ActionPreview kind='notification' title='Notification' icon={faCircleInfo as IconDefinition} details='That user could not be found.' />}
      />
    </ActionPreview>
  </ActionStack>
)

const actionSetPreview = reusableRoutineDefinitionPreview

const contextWindowPreview = (
  <ActionStack>
    <ActionPreview
      kind='variable'
      title='Variable'
      subtitle='Increment Variable'
      details={
        <>
          Increment <VariableToken>boop_count</VariableToken> by 1
        </>
      }
    />
    <ActionPreview kind='contextwindow' title='Context Window' icon={faWindowMaximize as IconDefinition}>
      <NestedActions caption='New context'>
        <ActionPreview
          kind='twitch'
          title='Twitch'
          subtitle='Chat Message'
          icon={faTwitch as IconDefinition}
          details={
            <>
              New count: <VariableToken>{'{boop_count}'}</VariableToken>
            </>
          }
        />
      </NestedActions>
    </ActionPreview>
  </ActionStack>
)

const queuePreview = (
  <QueueOptionsPreview
    options={[
      {
        title: 'Do Not Queue',
        description: 'This command will instantly execute.',
        icon: faBan as IconDefinition,
      },
      {
        title: 'Global Queue',
        description: 'Everything added to this queue will execute in order, one at a time.',
        icon: faGlobe as IconDefinition,
      },
      {
        title: 'Per-Command Queue',
        description: 'This command has its own queue and will execute in order, one at a time.',
        icon: faCircleExclamation as IconDefinition,
        selected: true,
      },
      {
        title: 'Custom Queue Group',
        description: 'Everything sharing the same queue name will execute in order, one at a time.',
        icon: faStar as IconDefinition,
      },
    ]}
  />
)

export const guides: GuideDefinition[] = [
  {
    slug: 'create-clip-and-post-it',
    title: 'Create a clip and post it in chat',
    tag: 'Twitch + Variables',
    summary: 'Create a Twitch clip, wait until Twitch finishes processing it, and use the finished clip information in whatever happens next.',
    icon: faClapperboard as IconDefinition,
    level: 'Start here',
    time: '5 minutes',
    outcome: [
      <>A chat command can create a Twitch clip with an optional title and duration.</>,
      <>Follow-up actions run only after Twitch has made the clip available.</>,
      <>
        Those actions can use <VariableToken>{'{clip:url}'}</VariableToken>, <VariableToken>{'{clip:title}'}</VariableToken>, and <VariableToken>{'{clip:duration}'}</VariableToken>.
      </>,
    ],
    sections: [
      {
        id: 'build',
        title: 'Build the action chain',
        paragraphs: [
          <>
            Add a <strong>Twitch → Create Clip</strong> action wherever you want the clip to be created. In the example below, the chat command uses a <VariableToken>{'{title}'}</VariableToken>{' '}
            argument and passes it directly into the optional clip title.
          </>,
          <>
            The duration can be a fixed number or a numeric variable. The important part is the nested <strong>If Clipping Succeeds</strong> section: put anything that needs the finished clip inside
            that section.
          </>,
        ],
        preview: clipAndChatPreview,
      },
      {
        id: 'message',
        title: 'Use the finished clip in chat',
        steps: [
          <>
            Inside <strong>If Clipping Succeeds</strong>, add a <strong>Twitch → Chat Message</strong> action.
          </>,
          <>
            Write the message you want and insert <VariableToken>{'{clip:url}'}</VariableToken> where the public Twitch clip link should appear.
          </>,
          <>
            You can also use the triggering user, for example: <VariableToken>{'{user:name}'}</VariableToken> or any other variables available to the original command.
          </>,
        ],
        note: <>Twitch creates clips asynchronously. Draco.Bot waits for the completed clip before running these nested actions, so this section may take several seconds to begin.</>,
      },
      {
        id: 'adapt',
        title: 'Ways to adapt it',
        paragraphs: [
          <>Instead of posting the URL directly, save it to a variable, write it to a file, announce it, or pass it into another Action Set.</>,
          <>The same pattern applies whenever an action produces values that only exist after it succeeds: keep the dependent work inside the action&apos;s success section.</>,
        ],
      },
    ],
    related: ['variables', 'actions-and-chains', 'queueing'],
  },
  {
    slug: 'show-obs-source-for-a-time',
    title: 'Show an OBS source for a set time',
    tag: 'OBS + Timing',
    summary: 'Turn an OBS source on, leave it visible for a predictable amount of time, turn it back off, and give OBS a moment to settle before another queued run can begin.',
    icon: faEye as IconDefinition,
    level: 'Start here',
    time: '3 minutes',
    outcome: [
      <>A source appears when the routine begins.</>,
      <>A Delay action keeps it visible for the amount of time you choose.</>,
      <>The source is turned back off before the action chain continues.</>,
      <>A short 100 ms Delay after disabling gives OBS time to finish the state change before another queued run can re-enable it.</>,
    ],
    sections: [
      {
        id: 'sequence',
        title: 'The four-action pattern',
        paragraphs: [
          <>
            This is one of the most useful patterns in Draco.Bot: <strong>enable → wait → disable → settle</strong>. The first Delay controls how long the source stays visible. The short final Delay
            gives OBS time to finish disabling it before the next queued run can enable it again.
          </>,
        ],
        preview: timedObsPreview,
      },
      {
        id: 'configure',
        title: 'Configure it',
        steps: [
          <>
            Add <strong>OBS → Enable Source</strong> and choose the scene and source.
          </>,
          <>
            Add a <strong>Delay</strong>. Five seconds is <VariableToken>5000</VariableToken> milliseconds.
          </>,
          <>
            Add <strong>OBS → Disable Source</strong> using the same scene and source.
          </>,
          <>
            Add one more short <strong>Delay</strong> after disabling. <VariableToken>100</VariableToken> milliseconds is a good default.
          </>,
        ],
        note: (
          <>
            That final delay is intentional. OBS can otherwise still be finishing the disable when the next queued copy of the routine starts, which can make an immediate disable → re-enable
            unreliable.
          </>
        ),
      },
      {
        id: 'reuse',
        title: 'Make it reusable when it starts repeating',
        paragraphs: [
          <>
            If you build this pattern more than once, move it into an Action Set and make the scene, source, and time parameters. Keep the final 100 ms settling delay inside the Action Set too, so
            every caller gets the OBS workaround automatically.
          </>,
        ],
        preview: timedObsActionSetPreview,
      },
    ],
    related: ['actions-and-chains', 'action-sets', 'queueing'],
  },
  {
    slug: 'per-viewer-counter',
    title: 'Keep a separate counter for each viewer',
    tag: 'Variables + Action Sets',
    summary: 'Use variables inside variable names to create counters dynamically for each viewer, interaction, or other piece of runtime data.',
    icon: faRotate as IconDefinition,
    level: 'Advanced',
    time: '10 minutes',
    outcome: [
      <>Each viewer gets an independent value without pre-creating hundreds of variables.</>,
      <>The same Action Set can power interactions such as pat, boop, bonk, hug, or anything else you invent.</>,
      <>Persistent variables can remember those counts from stream to stream.</>,
    ],
    sections: [
      {
        id: 'dynamic-name',
        title: 'Build the variable name from other values',
        paragraphs: [
          <>
            A managed variable name can itself contain variables. If the target username is available as <VariableToken>{'{@receiver:name}'}</VariableToken>, a name such as{' '}
            <VariableToken>boop_{'{@receiver:name}'}</VariableToken> becomes a different managed variable for each viewer.
          </>,
          <>
            If you also parameterize the interaction name, <VariableToken>{'{action}_{@receiver:name}'}</VariableToken> can power many commands with the same routine.
          </>,
        ],
        preview: viewerCounterPreview,
      },
      {
        id: 'increment',
        title: 'Increment first, then deliberately resolve the new value',
        steps: [
          <>
            Use <strong>Variable → Increment</strong> on the dynamic variable name.
          </>,
          <>
            If later nested actions need to see that newly changed value, put them inside a <strong>Context Window</strong>.
          </>,
          <>Inside the Context Window, use a Conditional Action to distinguish the first interaction from later ones, or simply print the updated count.</>,
        ],
        note: (
          <>The Context Window is intentional here. Normal queued actions keep the values they were loaded with; the Context Window creates the explicit boundary where descendants resolve again.</>
        ),
      },
      {
        id: 'persist',
        title: 'Choose whether the count should survive a restart',
        paragraphs: [
          <>
            Use a saved/permanent managed variable when the history should survive between streams. Use a temporary variable when the count only matters for the current run. The dynamic naming
            technique works with either.
          </>,
        ],
      },
    ],
    related: ['variables', 'action-sets', 'context-windows', 'conditional-actions'],
  },
  {
    slug: 'reuse-actions',
    title: 'Reuse the same actions in multiple places',
    tag: 'Reusable Automation',
    summary: 'Move repeated behavior into an Action Set, give it parameters, and call the same routine from commands, rewards, hotkeys, events, and other Action Sets.',
    icon: faList as IconDefinition,
    level: 'Intermediate',
    time: '7 minutes',
    outcome: [
      <>One canonical copy of the behavior instead of duplicated action chains.</>,
      <>Typed inputs make the routine reusable without hard-coding users, text, scenes, or other values.</>,
      <>Changing the Action Set updates every caller automatically.</>,
    ],
    sections: [
      {
        id: 'extract',
        title: 'Find the part you keep rebuilding',
        paragraphs: [<>If two commands contain the same three or four actions with only a username, message, source, or duration changed, that repeated section is a good Action Set candidate.</>],
        preview: reusableRoutineBodyPreview,
      },
      {
        id: 'parameters',
        title: 'Turn the changing pieces into parameters',
        steps: [
          <>Create an Action Set and add only the inputs the routine actually needs.</>,
          <>Choose the appropriate parameter type: text, number, username, scene, source, and so on.</>,
          <>Use those parameters as variables inside the Action Set&apos;s actions.</>,
        ],
        preview: reusableRoutineDefinitionPreview,
      },
      {
        id: 'call',
        title: 'Call it from anywhere',
        paragraphs: [<>Add the Action Set action to a chat command, reward, hotkey, event response, voice command, or another Action Set, then map the caller&apos;s values into its parameters.</>],
        preview: reusableRoutineCallPreview,
        note: <>A useful Action Set should hide repetition, not hide meaning. If a routine is only used once and has no meaningful parameters, keeping the actions inline is often simpler.</>,
      },
    ],
    related: ['action-sets', 'variables', 'actions-and-chains'],
  },
  {
    slug: 'actions-and-chains',
    title: 'Actions & action chains',
    tag: 'Building Block',
    summary: 'Understand the basic execution model: actions are small pieces of behavior, and an action chain runs them from top to bottom.',
    icon: faBolt as IconDefinition,
    level: 'Start here',
    time: '4 minutes',
    outcome: [
      <>Know where actions can be added and what their order means.</>,
      <>Understand why nested action sections exist.</>,
      <>Recognize when to keep a routine simple versus extracting it into an Action Set.</>,
    ],
    sections: [
      {
        id: 'mental-model',
        title: 'Think in small steps',
        paragraphs: [
          <>
            An action does one useful thing: send a message, change OBS, play audio, wait, modify a variable, call an Action Set, and so on. Put several together and Draco.Bot executes them in order.
          </>,
        ],
        preview: chainPreview,
      },
      {
        id: 'nesting',
        title: 'Some actions contain more actions',
        paragraphs: [
          <>
            Conditional Actions, Action Sets, Randomizers, Context Windows, Twitch clip success sections, and similar containers can hold nested action chains. Nesting expresses structure: “do these
            actions only here,” not a separate programming language you need to learn.
          </>,
        ],
      },
      {
        id: 'ordering',
        title: 'Ordering is part of the behavior',
        paragraphs: [
          <>
            If something needs to happen before another action, put it first. If something needs to wait for TTS, audio, a Delay, or a Twitch clip to finish, those actions act as natural timing
            boundaries before the next action runs.
          </>,
        ],
      },
    ],
    related: ['variables', 'conditional-actions', 'action-sets', 'queueing'],
  },
  {
    slug: 'variables',
    title: 'Variables',
    tag: 'Building Block',
    summary: 'Carry information from the trigger, integrations, Action Sets, and earlier actions into the rest of an automation.',
    icon: faLink as IconDefinition,
    level: 'Start here',
    time: '6 minutes',
    outcome: [
      <>Use runtime values instead of hard-coded text.</>,
      <>Understand triggering-user variables, Action Set parameters, managed variables, and action-produced variables.</>,
      <>Know why values normally stay stable after an automation is queued.</>,
    ],
    sections: [
      {
        id: 'insert',
        title: 'Variables are placeholders with values',
        paragraphs: [
          <>
            When a field supports variables, the variable picker inserts a token such as <VariableToken>{'{user:name}'}</VariableToken>. Draco.Bot resolves that token when the automation is loaded, so
            the queued routine keeps the context that originally triggered it.
          </>,
        ],
        preview: variablesPreview,
      },
      {
        id: 'sources',
        title: 'Where variables come from',
        steps: [
          <>The trigger can provide values such as the initiating user, command arguments, voice-command volume, event details, or reward information.</>,
          <>Action Sets expose their parameters as variables inside the Action Set.</>,
          <>Some actions produce new variables for their nested actions, such as the Twitch Create Clip action exposing information about the created clip.</>,
          <>Managed variables are values you create and update yourself, optionally saving them between streams.</>,
        ],
      },
      {
        id: 'nested',
        title: 'Variables can build other variable names',
        paragraphs: [
          <>
            Nested variables are useful when the identity of the managed variable depends on runtime data. A pattern such as <VariableToken>{'{action}_{@receiver:name}'}</VariableToken> can resolve to
            something like <VariableToken>boop_someviewer</VariableToken>.
          </>,
        ],
        note: <>If an earlier action changes a managed variable and later nested actions intentionally need the new value, that is when a Context Window becomes useful.</>,
      },
    ],
    related: ['context-windows', 'action-sets', 'per-viewer-counter'],
  },
  {
    slug: 'conditional-actions',
    title: 'Conditional Actions',
    tag: 'Building Block',
    summary: 'Choose which nested action chain should run based on users, variables, stream state, and other conditions.',
    icon: faCircleQuestion as IconDefinition,
    level: 'Intermediate',
    time: '6 minutes',
    outcome: [
      <>Run one action chain when a condition succeeds and another when it fails.</>,
      <>Use explicit blocking when a failed or successful branch should stop later actions in the parent chain.</>,
      <>Keep condition evaluation consistent with the values captured when the automation was loaded.</>,
    ],
    sections: [
      {
        id: 'basic',
        title: 'Compare a value and choose a branch',
        paragraphs: [
          <>
            A Conditional Action evaluates its rules once for the loaded automation, then prepares the matching branch. That keeps queued routines tied to the state that triggered them instead of
            silently changing meaning while they wait.
          </>,
        ],
        preview: conditionalPreview,
      },
      {
        id: 'branches',
        title: 'True and false are both useful',
        paragraphs: [
          <>The success branch can do the main work, while the failure branch can send a different message, log something, or do nothing at all. You do not have to populate both branches.</>,
        ],
      },
      {
        id: 'block',
        title: 'Use blocking as explicit control flow',
        paragraphs: [
          <>
            If the rest of the parent action chain should stop after one branch, use the Conditional Action&apos;s blocking behavior. An unexpected action error is not treated as a hidden “abort the
            routine” instruction; blocking is the explicit way to say that later actions should not run.
          </>,
        ],
      },
    ],
    related: ['variables', 'context-windows', 'actions-and-chains'],
  },
  {
    slug: 'action-sets',
    title: 'Action Sets',
    tag: 'Building Block',
    summary: 'Create reusable, parameterized routines that behave like your own custom Draco.Bot actions.',
    icon: faList as IconDefinition,
    level: 'Intermediate',
    time: '8 minutes',
    outcome: [
      <>Move repeated behavior into one reusable definition.</>,
      <>Give the routine typed parameters and use them as variables.</>,
      <>Call Action Sets from commands, rewards, events, hotkeys, voice commands, and other Action Sets.</>,
    ],
    sections: [
      {
        id: 'function',
        title: 'Treat an Action Set like a reusable function',
        paragraphs: [
          <>An Action Set is more than a saved group of actions. Its parameters let the caller provide the pieces that change while the Action Set owns the behavior that stays the same.</>,
        ],
        preview: actionSetPreview,
      },
      {
        id: 'types',
        title: 'Use parameter types to describe the contract',
        paragraphs: [
          <>
            A username parameter should be a username. A number should be numeric. Scenes and sources can use their dedicated types. The type makes the Action Set easier to call correctly and gives
            its internal editors the right variable choices.
          </>,
        ],
      },
      {
        id: 'compose',
        title: 'Action Sets can call Action Sets',
        paragraphs: [
          <>Small Action Sets can be composed into larger routines. That lets you build a vocabulary of stream behavior without creating one enormous monolithic routine for every command.</>,
        ],
        note: <>The best Action Sets usually describe something meaningful in your stream: “Greet User,” “Show Celebration,” “Freeze,” “Choose Giveaway Winner,” or “Perform Interaction.”</>,
      },
    ],
    related: ['reuse-actions', 'variables', 'actions-and-chains'],
  },
  {
    slug: 'context-windows',
    title: 'Context Windows',
    tag: 'Building Block',
    summary: 'Create an explicit point where nested actions resolve against updated values instead of the context captured earlier in the queued routine.',
    icon: faWindowMaximize as IconDefinition,
    level: 'Advanced',
    time: '7 minutes',
    outcome: [
      <>Understand why queued values normally do not change while a routine waits.</>,
      <>Know the narrow case where a Context Window is appropriate.</>,
      <>Avoid accidentally making time-of-execution state replace the original trigger state.</>,
    ],
    sections: [
      {
        id: 'default',
        title: 'The default behavior is intentionally stable',
        paragraphs: [
          <>
            When an automation is loaded and queued, Draco.Bot resolves its context so a routine that waits does not suddenly belong to a different user or see unrelated state when it eventually runs.
          </>,
        ],
      },
      {
        id: 'boundary',
        title: 'Use a Context Window when you intentionally changed something',
        paragraphs: [
          <>
            If an earlier action changes a managed variable and later nested actions need the new value, wrap those later actions in a Context Window. The Context Window is the explicit “resolve
            descendants here” boundary.
          </>,
        ],
        preview: contextWindowPreview,
      },
      {
        id: 'not-needed',
        title: 'You probably do not need one most of the time',
        paragraphs: [
          <>
            Do not add Context Windows merely because an automation is nested or queued. Use one when you can point to an earlier action in the same routine and say, “that action intentionally changed
            a value that these later actions need to re-read.”
          </>,
        ],
      },
    ],
    related: ['variables', 'per-viewer-counter', 'conditional-actions', 'queueing'],
  },
  {
    slug: 'queueing',
    title: 'Queueing behavior',
    tag: 'Building Block',
    summary: 'Choose whether top-level automations should start immediately, share one global queue, keep their own per-item queue, or coordinate through a custom queue group.',
    icon: faLayerGroup as IconDefinition,
    level: 'Intermediate',
    time: '7 minutes',
    outcome: [
      <>Understand all four queueing choices: Do Not Queue, Global Queue, Per-Command/Per-Response Queue, and Custom Queue Group.</>,
      <>Know why nested action groups are part of their parent routine rather than separate dashboard queues.</>,
      <>Understand how Draco.Bot keeps action failures from permanently stalling later work.</>,
    ],
    sections: [
      {
        id: 'modes',
        title: 'The four top-level choices',
        paragraphs: [
          <>
            Choose <strong>Do Not Queue</strong> when the item should start immediately. Use the <strong>Global Queue</strong> when many automations should share one ordered line. The{' '}
            <strong>Per-Command Queue</strong> (or Per-Response/Per-Item equivalent in other editors) gives each individual automation its own sequential queue. Use a{' '}
            <strong>Custom Queue Group</strong> when different commands or responses should share a named queue and never overlap with each other.
          </>,
        ],
        preview: queuePreview,
      },
      {
        id: 'nested',
        title: 'Nested queues are implementation, not a new user queue',
        paragraphs: [
          <>
            Conditional branches, Action Groups, Randomizers, Context Windows, and similar actions use internal action queues to run their children. They remain part of the parent routine and do not
            become separate top-level queue entries on the dashboard.
          </>,
        ],
      },
      {
        id: 'failure',
        title: 'One bad action should not poison the rest of the routine',
        paragraphs: [
          <>
            Expected integration failures are generally handled close to the integration. If an unexpected action exception still reaches the queue, Draco.Bot logs it, attempts cleanup, removes the
            failed action, and continues with later actions. Explicit blocking remains the mechanism for intentionally stopping a chain.
          </>,
        ],
      },
    ],
    related: ['actions-and-chains', 'conditional-actions', 'context-windows'],
  },
]

export const guideMap = new Map(guides.map((guide) => [guide.slug, guide]))
